import React from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { Form, Field, Formik, FormikProps, FieldProps } from 'formik';
import { SpaceBetween } from 'utils/style/extendable';
import { RootState } from 'store/store';
import messages from './Messages';
import Input from 'components/Input';
import Button from 'components/Button';
import notify from 'components/Notify';
import {
  IUserInfo,
  makhsanKillUserSessionsFn,
  makhsanUnblockUserFn,
  makhsanUpdateUserFn,
} from 'store/makhsanUserInfo/makhsanUserInfoSlice';
import {
  makhsanKillUserSessions,
  makhsanUnblockUser,
  makhsanUpdateUser,
} from 'store/makhsanUserInfo/makhsanUserInfoSlice';
import SelectChips from '../SelectChips';
import { RoleSelectContainer } from '../../containers/Tasks/CompanyManagement/styles';
import {
  makhsanGetAllRoles,
  onManageRole,
  IRole,
  IRoles,
  makhsanGetAllRolesFn,
  onManageRoleFn,
} from 'store/makhsanRoles/makhsanRolesSlice';
import { IChipItem } from '../SelectChips/SelectChips';
import { appDispatch } from 'store/store';
import { IThisUserState } from 'store/thisUser/thisUserSlice';

interface IStateToProps {
  thisUser: IThisUserState;
  roles: IRoles;
}

interface IDispatchToProps {
  updateUser: (
    deleted: boolean,
    firstName: string,
    lastName: string,
    password: string,
    active2FA: boolean,
    enable: boolean,
    validIps: string | undefined,
    timeout: number,
    userId: number
  ) => ReturnType<typeof makhsanUpdateUserFn>;
  unblockUser: (userId: number) => ReturnType<typeof makhsanUnblockUserFn>;
  killUserSessions: (
    userId: number
  ) => ReturnType<typeof makhsanKillUserSessionsFn>;
  getAllRoles: () => ReturnType<typeof makhsanGetAllRolesFn>;
  onManageRole: (
    userId: number,
    roleId: string,
    action: string
  ) => ReturnType<typeof onManageRoleFn>;
}

interface IOwnProps {
  user: IUserInfo;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

interface IComponentStates {
  saving: boolean;
  killing: boolean;
  savingRoles: boolean;
  unblocking: boolean;
  dummy?: boolean;
  initialValues: IFormValues;
}

interface IFormValues {
  firstName: string;
  lastName: string;
  password: string;
  validIps: string | undefined;
  timeout: number | undefined;
  active2fa: boolean;
  enabled: boolean;
}

class UserInfoBox extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  private unblockUser = () => {
    this.setState({ unblocking: true });
    this.props.unblockUser(this.props.user.user_id).then(
      () => {
        this.setState({ unblocking: false });
      },
      (reason: any) => {
        notify(reason.data.message, 'error');
        this.setState({ unblocking: false });
      }
    );
  };
  private killUserSessions = () => {
    this.setState({ killing: true });
    this.props.killUserSessions(this.props.user.user_id).then(
      () => {
        this.setState({ killing: false });
      },
      (reason: any) => {
        notify(reason.data.message, 'error');
        this.setState({ killing: false });
      }
    );
  };

  handleAddUserRole = (value: string | IChipItem) => {
    this.setState({ savingRoles: true });
    this.props
      .onManageRole(
        this.props.user.user_id,
        typeof value === 'string' ? value : value.id.toString(),
        'add'
      )
      .then(() => {
        this.setState({ savingRoles: false });
      });
  };
  handleDeleteUserRole = (value: string | IChipItem) => {
    this.setState({ savingRoles: true });
    this.props
      .onManageRole(
        this.props.user.user_id,
        typeof value === 'string' ? value : value.id.toString(),
        'remove'
      )
      .then(() => {
        this.setState({ savingRoles: false });
      });
  };
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      saving: false,
      killing: false,
      savingRoles: false,
      unblocking: false,
      initialValues: {
        firstName: props.user.first_name,
        lastName: props.user.last_name,
        password: '',
        active2fa: props.user.active2fa,
        timeout: props.user.timeout,
        validIps: props.user.validIps,
        enabled: props.user.enabled,
      },
    };
  }
  componentDidMount(): void {
    if (Object.values(this.props.roles).length === 0) {
      this.props.getAllRoles();
    }
  }

  componentDidUpdate(prevProps: Readonly<IComponentProps>): void {
    if (this.props.user !== prevProps.user)
      this.setState({
        initialValues: {
          firstName: this.props.user.first_name || '',
          lastName: this.props.user.last_name || '',
          password: '',
          timeout: this.props.user.timeout,
          validIps: this.props.user.validIps,
          active2fa: this.props.user.active2fa || false,
          enabled: this.props.user.enabled || true,
        },
      });
  }

  handleSave = ({
    firstName,
    lastName,
    password,
    enabled,
    active2fa,
    validIps,
    timeout,
  }: IFormValues) => {
    this.setState({ saving: true });
    this.props
      .updateUser(
        this.props.user.deleted,
        firstName,
        lastName,
        password,
        active2fa,
        enabled,
        validIps,
        timeout === undefined ? -1 : timeout,
        this.props.user.user_id
      )
      .then(
        () => {
          this.setState({ saving: false });
        },
        (message: any) => {
          notify(message.data.message, 'error');
          this.setState({ saving: false });
        }
      );
  };

  render() {
    const { user, roles } = this.props;
    const { killing, saving, unblocking } = this.state;
    const roleOptions = Object.values(roles).reduce(
      (list: IChipItem[], item: IRole) => {
        list.push({ id: item.roleName, title: item.roleName });
        return list;
      },
      [] as IChipItem[]
    );
    const currentValues = Object.values(roles).reduce(
      (list: IChipItem[], item: IRole) => {
        if (user.roles.includes(item.roleName))
          list.push({ id: item.roleName, title: item.roleName });
        return list;
      },
      [] as IChipItem[]
    );
    return (
      <React.Fragment>
        <div className="section">
          <Formik
            enableReinitialize
            onSubmit={this.handleSave}
            initialValues={this.state.initialValues}
            render={(formikBag: FormikProps<IFormValues>) => (
              // @ts-expect-error JSX error
              <Form>
                <Field
                  type="text"
                  name="firstName"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="text"
                      id="firstName"
                      label={messages.firstName}
                      placeholder={messages.firstName}
                      value={formikBag.values.firstName}
                    />
                  )}
                />
                <Field
                  type="text"
                  name="lastName"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="text"
                      id="lastName"
                      label={messages.lastName}
                      placeholder={messages.lastName}
                      value={formikBag.values.lastName}
                    />
                  )}
                />
                <Field
                  type="email"
                  name="email"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="text"
                      id="email"
                      label={messages.email}
                      placeholder={messages.email}
                      value={user.email || ''}
                      readOnly={true}
                      disabled={true}
                    />
                  )}
                />
                <Field
                  type="phone"
                  name="phone"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="text"
                      id="phone"
                      label={messages.phone}
                      placeholder={messages.phone}
                      value={user.phone || ''}
                      readOnly={true}
                      disabled={true}
                    />
                  )}
                />
                <Field
                  type="text"
                  name="password"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="password"
                      id="password"
                      label={messages.password}
                      placeholder={messages.password}
                      value={formikBag.values.password}
                    />
                  )}
                />
                <Field
                  type="text"
                  name="validIps"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="text"
                      id="validIps"
                      label={messages.validIps}
                      placeholder={messages.validIpsPlaceholder}
                      value={formikBag.values.validIps || '*.*.*.*'}
                    />
                  )}
                />
                <Field
                  type="text"
                  name="timeout"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="number"
                      id="timeout"
                      label={messages.timeout}
                      placeholder={messages.timeout}
                      value={formikBag.values.timeout || ''}
                    />
                  )}
                />
                <Field
                  type="checkbox"
                  name="enabled"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="checkbox"
                      id="enabled"
                      label={messages.enabled}
                      placeholder={messages.enabled}
                      checked={formikBag.values.enabled !== false}
                    />
                  )}
                />
                <Field
                  type="checkbox"
                  name="active2fa"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="checkbox"
                      id="active2fa"
                      label={messages.active2fa}
                      placeholder={messages.active2fa}
                      checked={formikBag.values.active2fa}
                    />
                  )}
                />
                {user.blocked && (
                  <span>
                    {messages.blocked} : {user.blockReason}
                  </span>
                )}
                <SpaceBetween style={{ margin: '2rem 0' }}>
                  <Button color="green" type="submit" loading={saving}>
                    {messages.save}
                  </Button>
                  <Button
                    color="red"
                    onClick={this.killUserSessions}
                    loading={killing}
                  >
                    {messages.killAllSessions}
                  </Button>
                  {user.blocked && (
                    <Button
                      color="red"
                      onClick={this.unblockUser}
                      loading={unblocking}
                    >
                      {messages.unblock}
                    </Button>
                  )}
                </SpaceBetween>
              </Form>
            )}
          />
        </div>

        <RoleSelectContainer>
          <SelectChips
            chips={roleOptions}
            removable={true}
            onAddChip={this.handleAddUserRole}
            placeholder={messages.addMemberRole}
            defaultChips={currentValues}
            onRemoveChip={this.handleDeleteUserRole}
          />
        </RoleSelectContainer>
      </React.Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state
) => {
  const roles = state.makhsanRoles;
  const thisUser = state.thisUser;
  return { thisUser, roles };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    updateUser: (
      deleted: boolean,
      firstName: string,
      lastName: string,
      password: string,
      active2FA: boolean,
      enable: boolean,
      validIps: string | undefined,
      timeout: number,
      userId: number
    ) => {
      return appDispatch(
        makhsanUpdateUser({
          deleted,
          firstName,
          lastName,
          password,
          active2fa: active2FA,
          enable,
          validIps,
          timeout,
          userId,
        })
      );
    },
    unblockUser: (userId: number) => {
      return appDispatch(makhsanUnblockUser({ userId }));
    },
    killUserSessions: (userId: number) => {
      return appDispatch(makhsanKillUserSessions({ userId }));
    },
    getAllRoles: () => {
      return appDispatch(makhsanGetAllRoles({}));
    },
    onManageRole: (userId: number, roleId: string, action: string) => {
      return appDispatch(onManageRole({ userId, role: roleId, action }));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserInfoBox);
